import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../utils/constants";

const api = axios.create({
	baseURL: API_URL,
	withCredentials: true,
});

// Interceptor to handle dynamic Content-Type
api.interceptors.request.use(
	(config) => {
		const accessToken = localStorage.getItem("accessToken");

		// Set Authorization header if token exists
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}

		// Conditionally set the Content-Type header based on data type
		if (config.data instanceof FormData) {
			config.headers["Content-Type"] = "multipart/form-data";
		} else {
			config.headers["Content-Type"] = "application/json";
		}

		return config;
	},
	(error) => Promise.reject(error)
);

// Custom hook to handle navigation
export const useAxiosInterceptors = () => {
	const navigate = useNavigate();
	const { user, logout, login, setPending } = useContext(AuthContext);

	useEffect(() => {
		const responseInterceptor = api.interceptors.response.use(
			(response) => response,
			async (error) => {
				const originalRequest = error.config;
				if (error.response?.status === 401) {
					const errorMessage = error.response.data.message;
					setPending(true);

					// Check if the error is due to an expired access token
					if (errorMessage === "Token required.") {
						logout();
						setPending(false);
					} else if (
						errorMessage === "Token expired." &&
						!originalRequest._retry
					) {
						originalRequest._retry = true;

						try {
							const data = await postApi("/auth/refreshToken");
							login({ user, accessToken: data.accessToken });

							api.defaults.headers.common[
								"Authorization"
							] = `Bearer ${data.accessToken}`;
							setPending(false);
							return api(originalRequest);
						} catch (refreshError) {
							console.error("Refresh token error:", refreshError);
							navigate("/auth");
							setPending(false);
							return Promise.reject(refreshError);
						}
					}
					setPending(false);
				}

				return Promise.reject(error);
			}
		);

		return () => {
			// Eject the interceptor when the component unmounts
			api.interceptors.response.eject(responseInterceptor);
		};
	}, [navigate, login, logout, user, setPending]);
};

// Example component using the custom hook
export const ApiComp = () => {
	useAxiosInterceptors();
	return <></>;
};

export const postApi = async (endpoint, data) => {
	try {
		const response = await api.post(endpoint, data);

		return response.data;
	} catch (error) {
		console.error("Error posting data:", error);
		throw error;
	}
};

export const putApi = async (endpoint, data) => {
	try {
		const response = await api.put(endpoint, data);
		return response.data;
	} catch (error) {
		console.error("Error updating data:", error);
		throw error;
	}
};

export const getApi = async (endpoint, params = {}) => {
	try {
		const query = new URLSearchParams(params).toString();
		const response = await api.get(`${endpoint}?${query}`);
		return response.data;
	} catch (error) {
		console.error("Error fetching data:", error);
		throw error;
	}
};

export const deleteApi = async (endpoint) => {
	try {
		const response = await api.delete(endpoint);
		return response.data;
	} catch (error) {
		console.error("Error deleting data:", error);
		throw error;
	}
};
